/**
 * 前端 API 薄層：統一處理「401 ＝ 未登入」同 JSON 錯誤。
 * ⚠️ 唔用第三方 fetch 包裝（保持零額外依賴）。
 * ⚠️ 欄位名一律 **camelCase** —— 後端 DTO 喺 `functions/api/_lib/*.ts` 做 snake→camel。
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** 由非 OK 回應砌出 `ApiError`（body 有 `error` 就用佢做訊息） */
async function toApiError(res: Response): Promise<ApiError> {
  let msg = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) msg = body.error;
  } catch {
    /* 非 JSON（例：Pages 404 頁）→ 保留 HTTP 碼 */
  }
  return new ApiError(msg, res.status);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

/**
 * 上載原始 bytes（貨品相）。
 * ⚠️ 唔可以經 `request()` —— 佢會強加 `Content-Type: application/json`（後端只收 image/*）。
 */
async function requestRaw<T>(path: string, file: File): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

export type Role = 'supplier' | 'logistics' | 'staff' | 'admin';

export interface Me {
  id: string;
  role: Role;
  username: string;
  supplierId: string | null;
  partnerName: string | null;
  /** ⭐ email（P1.5；`null` ＝ 未設定） */
  email?: string | null;
  /** ⭐ `true` ＝ 首次登入／未設定 email → 一定要去 `/setup` 設定 email ＋ 新密碼 */
  needsSetup?: boolean;
}

export interface Supplier {
  id: string;
  code: string;
  name: string;
  contactName: string | null;
  contactPhone: string | null;
  waNumber: string | null;
  active: number;
  createdAt: string;
}

export interface Item {
  id: string;
  supplierId: string;
  name: string;
  barcode: string;
  unitsPerCarton: number;
  cartonLCm: number;
  cartonWCm: number;
  cartonHCm: number;
  /** null ＝ 由尺寸自動算（⭐ 倉租計費基礎） */
  cbmPerCartonOverride: number | null;
  cbmPerCarton: number;
  perUnitCbm: number;
  weightKgPerCarton: number | null;
  /** ⭐ 安全庫存（箱；`null` ＝ 唔追蹤）—— P1.5-B 採購建議用 */
  safeLevel?: number | null;
  hasPhoto: boolean;
  active: number;
  createdAt: string;
}

export interface Setting {
  key: string;
  value: string;
  updatedAt: string;
}

// ── 入庫（P1-5） ──────────────────────────────────────────────────────
export type IntakeStatus = 'declared' | 'received' | 'verified' | 'discrepancy' | 'cancelled';

export interface IntakeLine {
  id: string;
  batchId: string;
  itemId: string;
  itemName: string | null;
  barcode: string | null;
  declaredCartons: number;
  receivedCartons: number;
  damagedCartons: number;
  cbmReceived: number;
  productionDate: string | null;
  expiryDate: string | null;
  note: string | null;
}

export interface IntakeBatch {
  id: string;
  batchNo: string;
  supplierId: string;
  declaredShipDate: string | null;
  /** ⭐ 起算日（＝收貨日） */
  receivedDate: string | null;
  status: IntakeStatus;
  declaredCbm: number;
  receivedCbm: number;
  handlingFee: number;
  createdBy: string | null;
  note: string | null;
  createdAt: string;
}

export interface IntakeLineInput {
  itemId: string;
  declaredCartons: number;
  productionDate?: string | null;
  expiryDate?: string | null;
  note?: string | null;
}

export interface IntakeVerifyLineInput {
  lineId: string;
  receivedCartons: number;
  damagedCartons: number;
  note?: string | null;
}

export type EvidenceEntityType = 'item' | 'intake_line' | 'outbound_order';
export type EvidenceKind = 'product_photo' | 'carton_photo' | 'damage' | 'delivery_note_chop' | 'other';

export interface SupplierInput {
  code: string;
  name: string;
  contactName?: string | null;
  contactPhone?: string | null;
  waNumber?: string | null;
}

export interface ItemInput {
  supplierId?: string;
  name: string;
  barcode: string;
  unitsPerCarton: number;
  cartonLCm: number;
  cartonWCm: number;
  cartonHCm: number;
  cbmPerCartonOverride?: number | null;
  weightKgPerCarton?: number | null;
}

// ── 庫存帳（P1-6） ──────────────────────────────────────────────────
/** 位置：`warehouse`／`damaged`／`office`／`supplier`／`written_off`／`roadshow:<單號>`／`store:<code>` */
export interface BalanceRow {
  itemId: string;
  /** '' ＝ 唔分批次 */
  batchId: string;
  location: string;
  cartons: number;
  units: number;
  cbm: number;
  itemName: string;
  barcode: string;
  unitsPerCarton: number;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  batchNo: string | null;
  updatedAt: string;
}

export interface InventorySummary {
  netCbm: number;
  /** ⭐ 計租基礎（`warehouse` ＋ `damaged`） */
  warehouseCbm: number;
  damagedCbm: number;
  byLocation: Array<{ location: string; cartons: number; units: number; cbm: number }>;
  skuCount: number;
  batchCount: number;
  invariantOk: boolean;
}

export interface Movement {
  id: string;
  seqNo: number;
  type: string;
  itemId: string;
  itemName: string;
  barcode: string;
  supplierId: string;
  supplierCode: string;
  batchId: string | null;
  batchNo: string | null;
  cartons: number;
  units: number;
  cbm: number;
  occurredAt: string;
  refType: string;
  refId: string | null;
  fromLoc: string;
  toLoc: string;
  destinationCode: string | null;
  actorUserId: string | null;
  actorName: string | null;
  note: string | null;
  createdAt: string;
}

/** 併參數成 query string（`undefined`／`''` 一律略過） */
function query(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export interface InventoryParams {
  location?: string;
  supplierId?: string;
  itemId?: string;
  batchId?: string;
  includeEmpty?: boolean;
}

export interface MovementParams {
  itemId?: string;
  type?: string;
  refType?: string;
  refId?: string;
  location?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

// ── 出庫（P1-7） ──────────────────────────────────────────────────
/** 出庫類型（契約：`@coeldery/outbound-order` `OUTBOUND_KINDS`） */
export type OutboundKind = 'direct_sale' | 'delivery';
/** 狀態（契約：`OUTBOUND_STATUSES`；`acknowledged` ＝ 接單 ＋ 執貨一次完成） */
export type OutboundStatus =
  | 'draft'
  | 'sent'
  | 'acknowledged'
  | 'delivered'
  | 'reconciled'
  | 'closed'
  | 'cancelled';
/** 取貨方式（契約：`DELIVERY_MODES`） */
export type DeliveryMode = 'self_pickup' | 'logistics';
/** 去向種類（`destinations.kind`；`disposition` 只喺場後結算用） */
export type DestinationKind = 'direct_sale' | 'delivery' | 'disposition';

export interface Destination {
  code: string;
  labelZhHk: string;
  labelEn: string;
  kind: string;
}

export interface OutboundOrder {
  id: string;
  orderNo: string;
  outboundKind: OutboundKind;
  requestedBy: string | null;
  /** 取貨時間（`YYYY-MM-DD` 或 `YYYY-MM-DD HH:mm`）—— WhatsApp 通知靠佢 */
  pickupAt: string | null;
  deliveryMode: DeliveryMode;
  destinationCode: string;
  destinationLabelZhHk: string;
  destinationLabelEn: string;
  consigneeName: string | null;
  consigneeAddress: string | null;
  /** 送貨碼（`kind=delivery` 才有） */
  deliveryCode: string | null;
  status: OutboundStatus;
  waStatus: string | null;
  waMessageId: string | null;
  waSentAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  pickedAt: string | null;
  /** 實際出庫 cbm（執貨時按實際箱數算；出帳基準） */
  outboundCbm: number;
  /** 差異摘要（`discrepancySummary()` 產生；null ＝ 冇差異） */
  pickDiscrepancy: string | null;
  deliveredAt: string | null;
  deliveredBy: string | null;
  note: string | null;
  createdAt: string;
  /** P1-9：有冇「客戶印章／簽名」送貨單相（`evidence.kind = delivery_note_chop`） */
  hasDeliveryNoteChop: boolean;
  /** ⭐ P1-9（`rules §七 H4`）：交收證據狀態 —— 一律由後端 `deliveryEvidence()` 裁決 */
  deliveryEvidence: { applicable: boolean; missing: boolean; missingKind: string | null };
}

/** 證據（`evidence` 表；**append-only**）—— 睇相一律經 `url`（session 串流，R2 私密） */
export interface EvidenceItem {
  id: string;
  kind: string;
  url: string;
  uploadedBy: string | null;
  uploadedAt: string;
  note: string | null;
}

export interface OutboundLine {
  id: string;
  orderId: string;
  itemId: string;
  itemName: string | null;
  barcode: string | null;
  batchId: string | null;
  batchNo: string | null;
  /** 申報箱數（唔准改；對帳基準） */
  cartons: number;
  cbm: number;
  /** null ＝ 未執 */
  pickedCartons: number | null;
  pickedNote: string | null;
  pickedAt: string | null;
  pickedBy: string | null;
  /** 倉內（`warehouse`）可用箱數 */
  warehouseCartons: number;
}

/** 執貨摘要（`pickSummary()`；`complete` ＝ 全部行都填咗） */
export interface PickSummary {
  itemCount: number;
  declaredCartons: number;
  pickedCartons: number;
  shortfallCartons: number;
  pendingLines: number;
  discrepancyLines: number;
  complete: boolean;
  hasDiscrepancy: boolean;
}

/** 詳情回應（GET／PATCH／pick／delivered 共用同一形狀） */
export interface OutboundDetail {
  ok: true;
  order: OutboundOrder | null;
  lines: OutboundLine[];
  summary: PickSummary;
  /** 執貨會去邊（`roadshow:<單號>`／`store:<代碼>`；後端砌，前端唔准自己駁字串） */
  toLoc: string | null;
  /** 而家可以去邊（後端 `nextStatuses()`；前端只准揀呢啲） */
  next: OutboundStatus[];
  statusAfterPick?: OutboundStatus;
}

/** 執貨清單行（`picklist` 專用：多咗到期日／批次生產日 —— FIFO 排序用） */
export interface PickListLine {
  lineId: string;
  itemId: string;
  itemName: string;
  barcode: string | null;
  batchId: string | null;
  batchNo: string | null;
  productionDate: string | null;
  expiryDate: string | null;
  cartons: number;
  cbm: number;
  pickedCartons: number | null;
  pickedNote: string | null;
  warehouseCartons: number;
}

export interface PickList {
  ok: true;
  order: OutboundOrder;
  lines: PickListLine[];
  summary: PickSummary;
  toLoc: string;
  /** 本身嘅 deep link（`<app.base_url>/o/<單號>`；未設定 ＝ null） */
  deepLink: string | null;
}

export interface OutboundParams {
  status?: OutboundStatus | '';
  kind?: OutboundKind | '';
  destinationCode?: string;
  /** WhatsApp deep link 用（`/o/:orderNo` → 搵返張單） */
  orderNo?: string;
}

export interface OutboundLineInput {
  itemId: string;
  batchId?: string | null;
  cartons: number;
}

export interface OutboundInput {
  outboundKind: OutboundKind;
  deliveryMode: DeliveryMode;
  destinationCode: string;
  pickupAt: string;
  consigneeName: string;
  consigneeAddress?: string | null;
  note?: string | null;
  lines: OutboundLineInput[];
}

export interface OutboundPatch {
  /** `cancel` ＝ 取消（draft／sent）；`close` ＝ 完單（`reconciled` → `closed`，P1-10） */
  action?: 'cancel' | 'close';
  deliveryMode?: DeliveryMode;
  pickupAt?: string | null;
  destinationCode?: string | null;
  consigneeName?: string | null;
  consigneeAddress?: string | null;
  note?: string | null;
  lines?: OutboundLineInput[];
}

export interface PickInputLine {
  lineId: string;
  pickedCartons: number;
  note?: string | null;
}

/** 發通知結果（`dryRun` 只回預覽，唔發） */
export interface SendResult {
  ok: boolean;
  dryRun?: true;
  templateName?: string;
  lang?: string;
  deepLink?: string | null;
  /** ⚠️ 一律**遮蔽**後嘅號碼 */
  recipients?: string[];
  results?: Array<{
    to: string;
    ok: boolean;
    wamid: string | null;
    errorCode: string | null;
    hint: string | null;
  }>;
  order?: OutboundOrder | null;
  lines?: OutboundLine[];
  summary?: PickSummary;
  error?: string;
}

// ── 場後結算（P1-10；`decisions/005` §2、`011`） ─────────────────────
/**
 * 受控選項（**由 DB 出**：`destinations` 表）—— 代碼係契約，中／英文標籤住喺 DB。
 * ⚠️ 前端只揀語言顯示（`optionLabel()`），**唔准**自己寫死去向清單。
 */
export interface LabelOption {
  code: string;
  labelZhHk: string;
  labelEn: string;
}

/** 「轉下一場」嘅目標：其他未完單嘅直銷場單（揀現有單 —— 「即場開新單」留 P2） */
export interface RoadshowTarget {
  orderNo: string;
  destinationCode: string;
  labelZhHk: string;
  labelEn: string;
}

/** 對數基準行（出庫行 ＋ 包裝 ＋ **實際執到**箱數） */
export interface ReconcileBaseLine {
  lineId: string;
  itemId: string;
  itemName: string;
  barcode: string | null;
  batchId: string | null;
  batchNo: string | null;
  declaredCartons: number;
  pickedCartons: number | null;
  /** ⭐ 對數基準 ＝ 實際執到嘅箱（未執貨嘅單唔會有結算） */
  outCartons: number;
  unitsPerCarton: number;
  /** 包裝 cbm（`sku-schema` 算好；前端只顯示，唔自己乘） */
  cbmPerCarton: number;
  perUnitCbm: number;
}

/** 已存結算行（箱 ＋ 散件） */
export interface ReconcileLine {
  id: string;
  outboundLineId: string;
  itemId: string;
  batchId: string | null;
  soldCartons: number;
  soldUnits: number;
  leftoverCartons: number;
  leftoverUnits: number;
  damagedCartons: number;
  damagedUnits: number;
  dispositionCode: string | null;
  /** 落點（後端用模組 `dispositionLocation()` 計 —— 前端唔准自己駁 `roadshow:`／`store:`） */
  toLoc: string | null;
  targetOrderNo: string | null;
  targetStoreCode: string | null;
  note: string | null;
  evidence: string | null;
}

/** 對數摘要（契約：`@coeldery/event-reconciliation` `ReconcileSummary`） */
export interface ReconcileSummary {
  itemCount: number;
  outCartons: number;
  soldCartons: number;
  leftoverCartons: number;
  damagedCartons: number;
  soldUnits: number;
  leftoverUnits: number;
  damagedUnits: number;
  balancedLines: number;
  unbalancedLines: number;
  missingDisposition: number;
  leftoverLines: number;
  /** ⭐ 全部行都對得上（＝ 准確認） */
  balanced: boolean;
}

export interface Reconciliation {
  id: string;
  orderId: string;
  venue: string | null;
  reportedBy: string | null;
  reportedAt: string | null;
  /** `draft`（未入帳，可以再改）／`confirmed`（已入帳，append-only） */
  status: string;
  note: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
  createdAt: string;
}

/** 結算產生嘅一筆帳（`SALE`／`TRANSFER`／`ADJUST`） */
export interface ReconcileMovement {
  id: string;
  type: string;
  itemId: string;
  itemName: string;
  cartons: number;
  units: number;
  cbm: number;
  fromLoc: string;
  toLoc: string;
  note: string;
}

/** 場後結算輸入行（**數量一律箱 ＋ 件**；`G1` 鐵律由後端 ＋ 模組驗） */
export interface ReconcileInputLine {
  lineId: string;
  soldCartons: number;
  soldUnits: number;
  leftoverCartons: number;
  leftoverUnits: number;
  damagedCartons: number;
  damagedUnits: number;
  dispositionCode?: string | null;
  targetOrderNo?: string | null;
  targetStoreCode?: string | null;
  note?: string | null;
}

export interface ReconcileDetail {
  ok: true;
  order: OutboundOrder;
  next: OutboundStatus[];
  /** 可以改／確認（`delivered` 且未確認） */
  canEdit: boolean;
  reconciliation: Reconciliation | null;
  lines: ReconcileLine[];
  /** 已存行嘅問題（空 ＝ 齊） */
  errors: string[];
  summary: ReconcileSummary | null;
  base: ReconcileBaseLine[];
  dispositions: LabelOption[];
  roadshowTargets: RoadshowTarget[];
  storeTargets: LabelOption[];
}

export interface ReconcileResult {
  ok: true;
  /** `true` ＝ 已確認入帳（狀態轉 `reconciled`）；`false` ＝ 只存草稿 */
  confirmed: boolean;
  order: OutboundOrder | null;
  reconciliationId: string;
  lines: ReconcileLine[];
  summary: ReconcileSummary;
  movements: ReconcileMovement[];
  movementIds: string[];
  next: OutboundStatus[];
}

/** 週費（倉租）狀態：確認之後唔准再改（`decisions/003` §5） */
export type WeekStatus = 'draft' | 'confirmed';

/** 一週倉租（P1-11；`openingCbm` ＝ ⭐ 計費基礎，見 `decisions/003` §4） */
export interface WeeklyFee {
  id: string;
  weekStart: string;
  weekEnd: string;
  openingCbm: number;
  /** 週內出庫 cbm（**只作參考**，唔影響金額） */
  outboundCbm: number;
  closingCbm: number;
  storageRate: number;
  storageAmount: number;
  handlingAmount: number;
  totalAmount: number;
  status: WeekStatus;
  generatedAt: string;
}

/** 費率（**唯一來源係後台 `settings`**；改費率只影響未產生嘅週期） */
export interface FeeRates {
  storagePerCbmWeek: number;
  handlingPerCbm: number;
  /** 每週結算日（1 ＝ 一 … 7 ＝ 日；`decisions/019` 起 ＝ 3 星期三） */
  settlementWeekday: number;
  /** 起租日（首批貨到倉日；該日所屬結算週期期初「含當日」）；未設定 ＝ `null` */
  firstStorageFrom?: string | null;
}

export interface WeeklyFeesResponse {
  ok: true;
  rates: FeeRates;
  /** ⚠️ `true` ＝ 設定嘅結算日唔係星期一 → 同期初制矛盾（產生週費會 409） */
  weekdayMismatch: boolean;
  currentWeekStart: string;
  fees: WeeklyFee[];
  totals: { storage: number; handling: number; total: number };
  /** 今週即時預覽（未產生／未確認都可以睇） */
  preview: Omit<WeeklyFee, 'id' | 'generatedAt' | 'status'> & {
    status: WeekStatus;
    existingStatus: WeekStatus | null;
    /** `feeIssues()` 嘅結果（空 ＝ OK） */
    issues: string[];
  };
  /** ⭐ P1.5-A：今週期初 cbm **拆到 SKU**（物流核對用；加埋 ＝ `preview.openingCbm`） */
  openingByItem?: OpeningByItem[];
  /** P1.5-A：確認紀錄（讀 `audit_log`） */
  acks?: FeeAck[];
}

// ── 儀表板／未完事項／週報（P1-12） ────────────────────────────────
/** 儀表板卡（睇唔到嘅卡 ＝ `null`；⚠️ 唔准當 0 睇 —— 0 同「唔關你事」係兩件事） */
export interface DashboardCards {
  pendingIntake: number | null;
  pendingOutbound: number | null;
  /** ⭐ 本週 cbm（計費基礎 ＝ 期初制；只 staff／admin） */
  weekCbm: number | null;
  unconfirmedFees: number | null;
  /** ⚠️ 未結算場次（`G4`） */
  unsettledEvents: number | null;
  /** ⚠️ 缺交收證據（`H4`） */
  missingChop: number | null;
  onSiteCbm: number | null;
}

/** 未完事項嘅單（精簡 DTO；詳情去出庫單頁） */
export interface PendingOrder {
  id: string;
  orderNo: string;
  outboundKind: OutboundKind | string;
  status: OutboundStatus | string;
  destinationCode: string;
  destinationLabel: string | null;
  deliveryCode: string | null;
  consigneeName: string | null;
  pickupAt: string | null;
  deliveredAt: string | null;
  /** 缺咗嘅 `evidence.kind`（只有「缺交收證據」類有值） */
  missingKind: string | null;
}

/** 在場庫存（按位置彙總） */
export interface OnSiteStock {
  location: string;
  /** `roadshow:<單號>` → 單號 */
  orderNo: string | null;
  cartons: number;
  units: number;
  cbm: number;
  skus: number;
}

export interface PendingCounts {
  unsettledEvents: number;
  /** ⚠️ 準確度上限 ＝ 掃描上限（後端 `limit`） */
  missingChop: number;
  onSiteCbm: number;
}

export interface PendingResponse {
  ok: true;
  unsettledEvents: PendingOrder[];
  missingChop: PendingOrder[];
  onSite: OnSiteStock[];
  counts: PendingCounts;
}

export interface DashboardResponse {
  ok: true;
  role: Role;
  currentWeekStart: string;
  cards: DashboardCards;
  /** 警示清單（每類最多 5 條） */
  pending: {
    unsettledEvents: PendingOrder[];
    missingChop: PendingOrder[];
    onSite: OnSiteStock[];
    counts: PendingCounts;
  };
  /** `true` ＝ 已經被供應商 scope 收窄 */
  scoped: boolean;
}

/** 帳本按移動類別彙總 */
export interface LedgerTypeSummary {
  type: string;
  count: number;
  cartons: number;
  units: number;
  cbm: number;
}

/** 該週收貨嘅批次 */
export interface WeekBatch {
  batchNo: string;
  supplierCode: string;
  supplierName: string;
  receivedDate: string | null;
  receivedCbm: number;
  handlingFee: number;
  status: string;
}

/** 週報（頁 14：倉租明細 ＋ 出入庫摘要；⭐ F1：數字同帳本對得上） */
export interface WeeklyReport {
  ok: true;
  weekStart: string;
  weekEnd: string;
  rates: FeeRates;
  weekdayMismatch: boolean;
  /** 已產生嘅週費（`null` ＝ 未產生） */
  fee: WeeklyFee | null;
  /** 即時計算（同週費頁 preview 同一支實作） */
  billing: Omit<WeeklyFee, 'id' | 'generatedAt'>;
  /** 已生成週費 vs 即時重算係唔係一致（`null` ＝ 未生成） */
  matchesGenerated: boolean | null;
  ledger: {
    byType: LedgerTypeSummary[];
    inbound: LedgerTypeSummary;
    outbound: LedgerTypeSummary;
    reconciliation: LedgerTypeSummary;
  };
  intakeBatches: WeekBatch[];
  issues: string[];
  pending: { unsettledEvents: number; missingChop: number; onSiteCbm: number };
}

// ── P1.5-A：我嘅資料／改密碼／庫存公式／倉租確認 ─────────────────────
/** 供應商公司資料（含 `attrs` 自訂欄位 —— 之後加欄位唔使 migration） */
export interface MySupplier {
  id: string;
  code: string;
  name: string;
  contactName: string | null;
  contactPhone: string | null;
  waNumber: string | null;
  active: number;
  createdAt: string;
  attrs: Record<string, unknown>;
}

/** 庫存公式行：`currentCbm ＝ inCbm − outCbm`（計租位置 warehouse ＋ damaged） */
export interface FlowRow {
  itemId: string;
  itemName: string;
  barcode: string;
  supplierId: string;
  supplierCode: string;
  unitsPerCarton: number;
  inCartons: number;
  inCbm: number;
  outCartons: number;
  outCbm: number;
  currentCartons: number;
  currentCbm: number;
  /** 由 `stock_balances` 讀嘅現時量（應該同 `current*` 一樣） */
  balanceCartons: number;
  balanceCbm: number;
  /** ⭐ `true` ＝ 公式同餘額一致（帳本自洽） */
  consistent: boolean;
}

export interface FlowResponse {
  ok: true;
  rows: FlowRow[];
  totals: {
    inCartons: number;
    inCbm: number;
    outCartons: number;
    outCbm: number;
    currentCartons: number;
    currentCbm: number;
    balanceCbm: number;
    consistent: boolean;
  };
  scoped: boolean;
  formula: string;
}

/** 倉租確認紀錄（讀 `audit_log`） */
export interface FeeAck {
  weekStart: string | null;
  feeId: string;
  at: string;
  by: string | null;
  note: string | null;
}

/** 逐個 SKU 嘅期初 cbm（物流核對用；加埋 ＝ `preview.openingCbm`） */
export interface OpeningByItem {
  itemId: string;
  itemName: string;
  barcode: string;
  supplierCode: string;
  cartons: number;
  cbm: number;
}

// ── P1.5-B：採購單（PO）＋ 安全庫存 ──────────────────────────────────
export type PurchaseStatus = 'draft' | 'sent' | 'ack' | 'shipped' | 'received' | 'closed' | 'cancelled';

export interface PurchaseOrder {
  id: string;
  poNo: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  status: PurchaseStatus;
  requestedBy: string | null;
  /** 職員要求到貨日 */
  requestedAt: string | null;
  /** ⭐ 供應商填嘅 ETA */
  expectedAt: string | null;
  sentAt: string | null;
  ackedAt: string | null;
  ackedBy: string | null;
  shippedAt: string | null;
  receivedAt: string | null;
  receivedBy: string | null;
  ackNote: string | null;
  note: string | null;
  createdAt: string;
  /** ⭐ 由後端模組出嘅下一步（前端唔准自己推） */
  next?: PurchaseStatus[];
}

export interface PurchaseLine {
  id: string;
  poId: string;
  itemId: string;
  itemName: string | null;
  barcode: string | null;
  /** 職員落單箱數（唔准改） */
  cartons: number;
  /** 供應商確認後箱數（`null` ＝ 未 ack） */
  cartonsAcked: number | null;
  note: string | null;
}

export interface PurchaseDetail {
  ok: true;
  order: PurchaseOrder;
  lines: PurchaseLine[];
  /** 由後端模組出嘅下一步（前端唔准自己推） */
  next: PurchaseStatus[];
  /** 出貨後自動開嘅發貨單 */
  intake: { id: string; batchNo: string; status: string } | null;
}

export interface PurchaseSuggestion {
  itemId: string;
  itemName: string;
  barcode: string;
  supplierId: string;
  supplierCode: string;
  /** 現時在倉（箱；＝ 入 − 出） */
  currentCartons: number;
  safeLevel: number | null;
}

export const api = {

  me: () => request<{ ok: true; user: Me }>('/api/auth/me'),
  /**
   * 首次登入設定（P1.5）：設定 email ＋ 新密碼。
   * ⚠️ 一定要傳 `currentPassword`（預設密碼）—— 後端會驗。
   */
  setupAccount: (body: { email: string; currentPassword: string; newPassword: string }) =>
    request<{ ok: true; setup: true; email: string }>('/api/auth/setup', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  login: (username: string, password: string) =>
    request<{ ok: true; user: Me; rateLimit: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  health: () => request<{ ok: true; db: string; wa: string }>('/api/health'),

  // ── 主檔（P1-4b） ────────────────────────────────────────────────
  suppliers: () => request<{ ok: true; suppliers: Supplier[] }>('/api/suppliers'),
  createSupplier: (body: SupplierInput) =>
    request<{ ok: true; id: string; code: string; supplier: Supplier }>('/api/suppliers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  items: (supplierId?: string) =>
    request<{ ok: true; items: Item[] }>(
      supplierId ? `/api/items?supplierId=${encodeURIComponent(supplierId)}` : '/api/items',
    ),
  createItem: (body: ItemInput) =>
    request<{ ok: true; item: Item }>('/api/items', { method: 'POST', body: JSON.stringify(body) }),
  uploadItemPhoto: (itemId: string, file: File) =>
    requestRaw<{ ok: true; key: string; bytes: number; contentType: string }>(
      `/api/items/${encodeURIComponent(itemId)}/photo`,
      file,
    ),
  /** 相片 URL（**要已登入 session** 才讀得到；R2 私密，冇公開 URL） */
  itemPhotoUrl: (itemId: string) => `/api/items/${encodeURIComponent(itemId)}/photo`,
  /**
   * 證據清單（`?entityType=outbound_order&entityId=…`）。
   * ⚠️ 後端唔回 `r2_key`；睇相用每項嘅 `url`（session 串流）。
   */
  evidence: (entityType: string, entityId: string) =>
    request<{ ok: true; entityType: string; entityId: string; total: number; evidence: EvidenceItem[] }>(
      `/api/evidence${query({ entityType, entityId })}`,
    ),
  /**
   * ⭐ P1-9：**送貨單**（獨立列印頁 —— 開新分頁，用瀏覽器列印／另存 PDF）。
   * `?format=json` ＝ 回文件模型（測試／app 內預覽用），唔加 ＝ HTML。
   */
  deliveryNoteUrl: (id: string, lang: string, format?: 'json') =>
    `/api/outbound-orders/${encodeURIComponent(id)}/delivery-note${query({ lang, format })}`,

  // ── 設定（P1-4b；只准 admin 寫） ─────────────────────────────────
  settings: () => request<{ ok: true; settings: Setting[] }>('/api/settings'),
  saveSettings: (patch: Record<string, string>) =>
    request<{ ok: true; changed: string[]; settings: Setting[] }>('/api/settings', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // ── 入庫（P1-5b） ────────────────────────────────────────────────
  intakes: (status?: IntakeStatus | '') =>
    request<{ ok: true; batches: IntakeBatch[] }>(
      status ? `/api/intakes?status=${encodeURIComponent(status)}` : '/api/intakes',
    ),
  intake: (id: string) =>
    request<{ ok: true; batch: IntakeBatch; lines: IntakeLine[] }>(`/api/intakes/${encodeURIComponent(id)}`),
  createIntake: (body: {
    supplierId?: string;
    declaredShipDate?: string | null;
    note?: string | null;
    lines: IntakeLineInput[];
  }) =>
    request<{ ok: true; batch: IntakeBatch; lines: IntakeLine[] }>('/api/intakes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** ⭐ 收貨對數（一按即入帳：好貨 → warehouse、損壞 → damaged） */
  verifyIntake: (
    id: string,
    body: { receivedDate: string; lines: IntakeVerifyLineInput[]; damageEvidenceIds: string[] },
  ) =>
    request<{
      ok: true;
      batch: IntakeBatch;
      lines: IntakeLine[];
      movements: string[];
      handlingRate: number;
      status: string;
    }>(`/api/intakes/${encodeURIComponent(id)}/verify`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** 上載證據（原始 bytes；R2 私密）→ 回 `id`，收貨對數時要引用 */
  uploadEvidence: (
    target: { entityType: EvidenceEntityType; entityId: string; kind: EvidenceKind; note?: string },
    file: File,
  ) => {
    const qs = new URLSearchParams({
      entityType: target.entityType,
      entityId: target.entityId,
      kind: target.kind,
    });
    if (target.note) qs.set('note', target.note);
    return requestRaw<{ ok: true; id: string; key: string; bytes: number }>(
      `/api/evidence?${qs.toString()}`,
      file,
    );
  },

  // ── 庫存帳（P1-6） ────────────────────────────────────────────────
  /** 庫存餘額（供應商只會見到自己嘅貨；後端 scope，前端唔使傳 supplierId） */
  inventory: (params: InventoryParams = {}) =>
    request<{ ok: true; rows: BalanceRow[]; summary: InventorySummary; scoped: boolean }>(
      `/api/inventory${query({ ...params, includeEmpty: params.includeEmpty ? '1' : undefined })}`,
    ),
  /** 庫存帳本（append-only；新 → 舊） */
  movements: (params: MovementParams = {}) =>
    request<{
      ok: true;
      movements: Movement[];
      total: number;
      limit: number;
      offset: number;
      scoped: boolean;
    }>(`/api/movements${query({ ...params })}`),

  // ── 出庫（P1-7） ────────────────────────────────────────────────
  /**
   * 去向代碼（直銷場／送貨目的地）。⚠️ **唔准寫死喺前端** —— 加／停用去向要改 DB，唔應該改 code。
   * `kind` 唔傳 ＝ 回全部啟用中嘅去向。
   */
  destinations: (kind?: DestinationKind) =>
    request<{ ok: true; destinations: Destination[] }>(`/api/destinations${query({ kind })}`),
  /** 出庫單列表（**供應商 403**：出庫流程冇供應商份 —— 後端把關） */
  outboundOrders: (params: OutboundParams = {}) =>
    request<{ ok: true; orders: OutboundOrder[] }>(`/api/outbound-orders${query({ ...params })}`),
  outboundOrder: (id: string) =>
    request<OutboundDetail>(`/api/outbound-orders/${encodeURIComponent(id)}`),
  createOutbound: (body: OutboundInput) =>
    request<OutboundDetail & { declaredCbm: number }>('/api/outbound-orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** 改單（**只准 `draft`**）或者 `{action:'cancel'}` 取消 */
  updateOutbound: (id: string, patch: OutboundPatch) =>
    request<OutboundDetail>(`/api/outbound-orders/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  /** 發 WhatsApp 出庫通知（V4 範本）；`dryRun` ＝ 只回 payload 預覽，唔發 */
  sendOutbound: (id: string, dryRun = false) =>
    request<SendResult>(`/api/outbound-orders/${encodeURIComponent(id)}/send`, {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    }),
  /** 執貨清單（現場照住執；**按到期日 FIFO 排**） */
  picklist: (id: string) =>
    request<PickList>(`/api/outbound-orders/${encodeURIComponent(id)}/picklist`),
  /** 執貨（記實際箱數 ＋ 即時出帳 `OUT`；要 `sent` 先准） */
  pickOutbound: (
    id: string,
    body: { occurredAt?: string | null; lines: PickInputLine[] },
  ) =>
    request<OutboundDetail & { discrepancy: string | null; movements: string[] }>(
      `/api/outbound-orders/${encodeURIComponent(id)}/pick`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** 交收完成（`delivery` → `closed`；`direct_sale` → `delivered`，仍要場後結算） */
  deliverOutbound: (id: string, body: { deliveredAt?: string | null; note?: string | null } = {}) =>
    request<OutboundDetail>(`/api/outbound-orders/${encodeURIComponent(id)}/delivered`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── 場後結算（P1-10） ────────────────────────────────────────────
  /**
   * 場後結算：對數基準（**實際執到**箱數）＋ 受控清單（去向／目標單／店）。
   * 只有直銷場單（`direct_sale`）得 —— 送貨單送達即完單（後端 400）。
   */
  reconciliation: (id: string) =>
    request<ReconcileDetail>(`/api/outbound-orders/${encodeURIComponent(id)}/reconciliation`),
  /**
   * 場後結算存檔：`confirm` 唔填／`false` ＝ **存草稿（未入帳）**；
   * `true` ＝ **確認入帳**（`SALE`／`TRANSFER`／`ADJUST`）＋ `delivered → reconciled`。
   * ⚠️ 兩種都要逐項填齊而且對得上（`G1`）—— 草稿都唔准存假帳。
   */
  saveReconciliation: (
    id: string,
    body: {
      reportedAt?: string | null;
      venue?: string | null;
      note?: string | null;
      confirm?: boolean;
      lines: ReconcileInputLine[];
    },
  ) =>
    request<ReconcileResult>(`/api/outbound-orders/${encodeURIComponent(id)}/reconciliation`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** 完單（`reconciled → closed`）；⚠️ **職員／管理員**（後端一樣擋） */
  closeOutbound: (id: string) =>
    request<OutboundDetail>(`/api/outbound-orders/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'close' }),
    }),

  // ── 週費／倉租（P1-11） ──────────────────────────────────────────
  /** 週費清單 ＋ 費率 ＋ 今週預覽（**只准 staff／admin**；供應商／物流 403 —— 帳目係商業資料） */
  weeklyFees: () => request<WeeklyFeesResponse>('/api/weekly-fees'),
  /**
   * 產生／重算某週倉租（唔填 `weekStart` ＝ **今週**）。
   * ⚠️ 只准星期一、唔准未來週；已有 `confirmed` → 409（要更正就另開調整紀錄）。
   */
  generateWeeklyFee: (weekStart?: string) =>
    request<{ ok: true; regenerated: boolean; fee: WeeklyFee | null }>('/api/weekly-fees/generate', {
      method: 'POST',
      body: JSON.stringify(weekStart ? { weekStart } : {}),
    }),
  /** 確認（`draft → confirmed`；確認之後金額唔准再改） */
  confirmWeeklyFee: (id: string) =>
    request<{ ok: true; confirmed: true; fee: WeeklyFee | null }>(
      `/api/weekly-fees/${encodeURIComponent(id)}/confirm`,
      { method: 'POST' },
    ),

  // ── 儀表板／未完事項／週報（P1-12） ──────────────────────────────
  /** 儀表板（頁 2）：卡 ＋ 警示清單（每類最多 5 條）；睇唔到嘅卡係 `null` */
  dashboard: () => request<DashboardResponse>('/api/dashboard'),
  /** 未完事項一覽（頁 17；**只准物流／職員／管理員** —— 供應商 403） */
  pending: (limit?: number) =>
    request<PendingResponse>(`/api/pending${limit ? `?limit=${encodeURIComponent(String(limit))}` : ''}`),
  /** 週報（頁 14；**只准職員／管理員**）；`weekStart` 任意日 → 後端歸該週星期一 */
  weeklyReport: (weekStart?: string) =>
    request<WeeklyReport>(
      `/api/reports/weekly${weekStart ? `?weekStart=${encodeURIComponent(weekStart)}` : ''}`,
    ),

  // ── P1.5-A：我嘅資料／改密碼／庫存公式／倉租確認 ────────────────────
  /** 我嘅資料（供應商睇自己；admin／staff 可以傳 `supplierId`） */
  mySupplier: (supplierId?: string) =>
    request<{ ok: true; supplier: MySupplier }>(
      `/api/me${supplierId ? `?supplierId=${encodeURIComponent(supplierId)}` : ''}`,
    ),
  /** 改我嘅資料（供應商只可以改自己；`code` 唯讀） */
  updateMySupplier: (patch: {
    name?: string;
    contactName?: string | null;
    contactPhone?: string | null;
    waNumber?: string | null;
    attrs?: Record<string, unknown> | null;
  }) =>
    request<{ ok: true; supplier: MySupplier }>('/api/me', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  /** 改自己密碼（⚠️ 會撤銷自己其他 session） */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true; changed: true }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  /** 庫存公式：`入 − 出 ＝ 現時`（三個角色共用；供應商只見到自己） */
  inventoryFlow: () => request<FlowResponse>('/api/inventory/flow'),
  /** 物流／內部：核對倉租後確認（**唔改任何金額**；記 who／幾時／備註） */
  ackWeeklyFee: (id: string, note?: string) =>
    request<{ ok: true; acked: true; feeId: string; weekStart: string; note: string | null }>(
      `/api/weekly-fees/${encodeURIComponent(id)}/ack`,
      { method: 'POST', body: JSON.stringify(note ? { note } : {}) },
    ),

  // ── P1.5-B：採購單（PO）＋ 安全庫存 ────────────────────────────────
  /** 採購單列表（供應商只回自己；可 `?status=`） */
  purchaseOrders: (status?: PurchaseStatus) =>
    request<{ ok: true; orders: PurchaseOrder[]; scoped: boolean }>(
      `/api/purchase-orders${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  /** 單張採購單（＋行＋下一步＋關聯發貨單） */
  purchaseOrder: (id: string) =>
    request<PurchaseDetail>(`/api/purchase-orders/${encodeURIComponent(id)}`),
  /** 落單（職員／管理員；`draft`） */
  createPurchaseOrder: (body: {
    supplierId: string;
    requestedAt?: string | null;
    note?: string | null;
    lines: Array<{ itemId: string; cartons: number; note?: string | null }>;
  }) =>
    request<{ ok: true; id: string; poNo: string; status: PurchaseStatus }>('/api/purchase-orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** 狀態機動作：`send`｜`ack`｜`ship`｜`receive`｜`close`｜`cancel` */
  purchaseAction: (
    id: string,
    action: 'send' | 'ack' | 'ship' | 'receive' | 'close' | 'cancel',
    body?: { expectedAt?: string; ackNote?: string | null; lines?: Array<{ lineId: string; cartonsAcked: number }> },
  ) =>
    request<{ ok: true; id: string; status: PurchaseStatus }>(
      `/api/purchase-orders/${encodeURIComponent(id)}/${action}`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  /** 安全庫存建議（職員／管理員）：現時在倉 ＋ `safeLevel` ＋ 要補貨清單 */
  purchaseSuggestions: () =>
    request<{
      ok: true;
      items: PurchaseSuggestion[];
      shortage: Array<{ itemId: string; itemName: string; currentCartons: number; safeLevel: number; suggestCartons: number }>;
    }>('/api/purchase-orders/suggestions'),
};
